import * as React from "react";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import { 
  Box, Paper, Fab, FormControl, InputLabel, Select, MenuItem, 
  OutlinedInput, Chip, Button 
} from "@mui/material";
import api from "../../services/api";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { useStoreState, useStoreActions } from "easy-peasy";
import { materialTableLocalization } from "../../config";
import Fornecedor from "../Fornecedor/fornecedor";
import { useEffect } from "react";
import { PORTE } from "../../helpers/porte";

const LASTQUERY = "last_search_query";
const LASTPAGE = "last_page_query";
const LASTFILTROS = "last_filtros";

const Processos = () => {
  let user = useStoreState((state) => state.auth.user);
  let startLoading = useStoreActions((actions) => actions.nav.startLoading);
  let stopLoading = useStoreActions((actions) => actions.nav.stopLoading);

  const [columns, setColumns] = React.useState([]);
  const [programas, setProgramas] = React.useState([]);
  const [ufs, setUfs] = React.useState([
    "AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT",
    "PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"
  ]);
  const portes = [PORTE.MICRO, PORTE.PEQUENA, PORTE.MEDIA, PORTE.GRANDE];
  const [estagios, setEstagios] = React.useState([]);

  const [filtroPortes, setFiltroPortes] = React.useState([]);
  const [filtroEstagios, setFiltroEstagios] = React.useState([]);
  const [filtroProgramas, setFiltroProgramas] = React.useState([]);
  const [filtroUfs, setFiltroUfs] = React.useState([]);
  const [tableKey, setTableKey] = React.useState(Date.now());

  useEffect(() => {
    const saved = sessionStorage.getItem(LASTFILTROS);
    if (saved) {
      const filtros = JSON.parse(saved);
      setFiltroProgramas(filtros.programas || []);
      setFiltroUfs(filtros.ufs || []);
      setFiltroPortes(filtros.portes || []);
      setFiltroEstagios(filtros.estagios || []);
    }
  }, []);

  useEffect(() => {
    const filtros = {
      programas: filtroProgramas,
      ufs: filtroUfs,
      portes: filtroPortes,
      estagios: filtroEstagios,
    };
    sessionStorage.setItem(LASTFILTROS, JSON.stringify(filtros));
  }, [filtroProgramas, filtroUfs, filtroPortes, filtroEstagios]);

  useEffect(() => {
    api.http.get("/programas").then(res => {
      if (user.programas && user.programas.length > 0) {
        const ids = user.programas.map(p => p.id);
        setProgramas(res.data.filter(p => ids.includes(p.id)));
      } else {
        setProgramas(res.data);
      }
    });
    api.http.get("/estagios").then(res => setEstagios(res.data));
  }, [user]);

  const handleChangeProgramas = (event) => {
    const value = event.target.value;
    setFiltroProgramas(typeof value === "string" ? value.split(",") : value);
  };

  const handleChangeUfs = (event) => {
    const value = event.target.value;
    setFiltroUfs(typeof value === "string" ? value.split(",") : value);
  };

  const limparFiltros = () => {
    setFiltroProgramas([]);
    setFiltroUfs([]);
    setFiltroPortes([]);
    setFiltroEstagios([]);

    sessionStorage.removeItem(LASTFILTROS);

    sessionStorage.removeItem(LASTQUERY);
    sessionStorage.removeItem(LASTPAGE);

    setTableKey(Date.now()); 
  };

  const loadProcessos = React.useCallback(async (query) => {
    startLoading();
    try {
      const programaIdsToSend =
        filtroProgramas.length > 0
          ? filtroProgramas
          : (user.programas ? user.programas.map(p => p.id) : []);

      const orderByField = query.orderBy?.field;
      const orderByApi = orderByField === "cidadeEstado" ? "cidade" : orderByField;

      let getProcessos = (
        await api.http.get("/processos", {
          params: {
            page: query.page + 1,
            pageSize: query.pageSize,
            search: query.search,
            orderBy: orderByApi || undefined,
            orderDirection: query.orderDirection || undefined,
            userId: user.id,
            roleId: user.roleId,
            programaIds: programaIdsToSend,
            ufs: filtroUfs,
            portes: filtroPortes,
            estagios: filtroEstagios,
          },
        })
      ).data;

      sessionStorage.setItem(LASTQUERY, query.search);
      sessionStorage.setItem(LASTPAGE, getProcessos.meta.current_page - 1);

      let estagiosData = (await api.http.get("/estagios")).data;

      stopLoading();
      return {
        data: await Promise.all(getProcessos.data.map(async (data) => {
          let concatEstagio = "";
          const matchingEstagio = estagiosData.find(estagio => estagio.id === data.estagio);
          if (matchingEstagio) concatEstagio = matchingEstagio.nome;

          return {
            ...data,
            cidadeEstado: `${data.cidade || ""}/${data.estado || ""}`,
            estagio: concatEstagio,
            // porte salvo, que é por faturamento - recalcular aqui (antes era pelo nº
            // de funcionários) fazia a coluna discordar do filtro, que usa o valor salvo
            porte: data.porte || "",
            programaNome: data.programa ? data.programa.nome : "-",
          };
        })),
        page: getProcessos.meta.current_page - 1,
        totalCount: getProcessos.meta.total,
      };
    } catch (error) {
      stopLoading({ message: error.toString(), severity: "error" });
      return { data: [], page: 0, totalCount: 0 };
    }
  }, [user, filtroProgramas, filtroUfs, filtroPortes, filtroEstagios, startLoading, stopLoading]);

  let navigate = useNavigate();

  const loadColumns = async (mount) => {
    if (mount.isMounted) {
      setColumns([
        {
          field: "id",
          align: "right",
          title: "Cod. Processo",
          cellStyle: { minWidth: 30, maxWidth: 60 },
          headerStyle: { minWidth: 30, maxWidth: 60 },
        },
        {
          field: "razaoSocial",
          align: "left",
          title: "Razão Social",
          cellStyle: { minWidth: 200, maxWidth: 300 },
          headerStyle: { minWidth: 200, maxWidth: 300 },
        },
        {
          field: "cidadeEstado",
          align: "center",
          title: "Cidade/Estado(UF)",
          cellStyle: { minWidth: 50, maxWidth: 100 },
          headerStyle: { minWidth: 60, maxWidth: 120 },
        },
        {
          field: "porte",
          align: "left",
          title: "Porte",
        },
        {
          field: "estagio",
          align: "left",
          title: "Estágio",
        },
        {
          field: "programaNome",
          align: "center",
          title: "Programa",
        },
        {
          field: "createdAt",
          align: "center",
          title: "Data do interesse",
          type: "date",
        },
        {
          field: "updatedAt",
          align: "center",
          title: "Atualização",
          type: "date",
        },
      ]);
    }
  };

  React.useEffect(() => {
    let mount = { isMounted: true };
    loadColumns(mount);
    return () => { mount.isMounted = false; };
  }, []);

  const onRowClick = (event, rowData) => {
    navigate("/processo/" + rowData.id);
  };

  const showAddProcesso = () => {
    return [
      "superAdmin", "consultorEE", "consultorMercado",
      "validadorIndSENAI", "gestorSENAI"
    ].includes(user.roleId);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper variant="outlined" className="center-box" sx={{ width: "100%", my: 3 }}>
        {user.roleId === "fornecedor" ? (
          <Fornecedor />
        ) : (
          <>
            <Box sx={{ p:2, mb:2 }}>
              <h3>Filtros</h3>
              <Box sx={{ 
                display: "grid", 
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }, 
                gap: 2 
              }}>
                {/* Programas */}
                <FormControl fullWidth>
                  <InputLabel>Programas</InputLabel>
                  <Select
                    multiple
                    value={filtroProgramas}
                    onChange={handleChangeProgramas}
                    input={<OutlinedInput label="Programas" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((val) => {
                          const nome = programas.find(p => p.id === val)?.nome || val;
                          return (
                            <Chip
                              key={val}
                              label={nome}
                              onMouseDown={(e) => e.stopPropagation()}
                              onDelete={() => {
                                setFiltroProgramas(filtroProgramas.filter(p => p !== val));
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {programas.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* UFs */}
                <FormControl sx={{ minWidth:200 }}>
                  <InputLabel>Estados (UF)</InputLabel>
                  <Select
                    multiple
                    value={filtroUfs}
                    onChange={handleChangeUfs}
                    input={<OutlinedInput label="Estados (UF)" />}
                    renderValue={(selected) => (
                      <Box sx={{ display:"flex", flexWrap:"wrap", gap:0.5 }}>
                        {selected.map((uf) => (
                          <Chip 
                            key={uf} 
                            label={uf} 
                            onMouseDown={(e) => e.stopPropagation()}
                            onDelete={() => {
                              setFiltroUfs(filtroUfs.filter(p => p !== uf));
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {ufs.map((uf) => (
                      <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Portes */}
                <FormControl sx={{ minWidth:200 }}>
                  <InputLabel>Porte</InputLabel>
                  <Select
                    multiple
                    value={filtroPortes}
                    onChange={(e) => setFiltroPortes(e.target.value)}
                    input={<OutlinedInput label="Porte" />}
                    renderValue={(selected) => (
                      <Box sx={{ display:"flex", flexWrap:"wrap", gap:0.5 }}>
                        {selected.map((porte) => (
                          <Chip 
                            key={porte} 
                            label={porte} 
                            onMouseDown={(e) => e.stopPropagation()}
                            onDelete={() => {
                              setFiltroPortes(filtroPortes.filter(p => p !== porte));
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {portes.map((porte) => (
                      <MenuItem key={porte} value={porte}>{porte}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Estágios */}
                <FormControl sx={{ minWidth:250 }}>
                  <InputLabel>Estágios</InputLabel>
                  <Select
                    multiple
                    value={filtroEstagios}
                    onChange={(e) => setFiltroEstagios(e.target.value)}
                    input={<OutlinedInput label="Estágios" />}
                    renderValue={(selected) => (
                      <Box sx={{ display:"flex", flexWrap:"wrap", gap:0.5 }}>
                        {selected.map((id) => {
                          const est = estagios.find(e => e.id === id);
                          return (
                            <Chip
                              key={id}
                              label={est ? est.nome : id}
                              onMouseDown={(e) => e.stopPropagation()}
                              onDelete={() => {
                                setFiltroEstagios(filtroEstagios.filter(p => p !== id));
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {estagios.map((est) => (
                      <MenuItem key={est.id} value={est.id}>{est.nome}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <MaterialTable
              key={tableKey + filtroProgramas.join(",") + filtroUfs.join(",") + filtroPortes.join(",") + filtroEstagios.join(",")}
              columns={columns}
              data={loadProcessos}
              title=""
              onRowClick={onRowClick}
              localization={materialTableLocalization}
              options={{
                searchText: sessionStorage.getItem(LASTQUERY) || "",
                initialPage: sessionStorage.getItem(LASTPAGE)
                  ? parseInt(sessionStorage.getItem(LASTPAGE))
                  : 0,
              }}
              components={{
                Toolbar: (props) => {
                  const toolbarProps = { ...props, showTitle: false };

                  return (
                    <Box 
                      sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "space-between", 
                        px: 2, 
                        py: 1 
                      }}
                    >
                      <Box sx={{ fontSize: "1.25rem", fontWeight: 500 }}>
                        Processos
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <MTableToolbar {...toolbarProps} />
                        <Button
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={limparFiltros}
                        >
                          Limpar filtros
                        </Button>
                      </Box>
                    </Box>
                  );
                },
              }}
            />

            {showAddProcesso() && (
              <Fab
                color="primary"
                aria-label="novo processo"
                onClick={() => navigate("/processo/")}
              >
                <AddIcon />
              </Fab>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default Processos;
